import { TabPanel } from "@similarweb/ui-components";
import _ from "lodash";
import {
    ErrorComponent,
    NoDataComponent,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Components/EmptyState";
import { FiltersRow } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Components/FiltersRow";
import { SearchTrafficInsights } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/SearchTrafficInsights/SearchTrafficInsights";
import type { FunctionComponent } from "react";
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { getMonthToDateEndPointValue } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";
import { TabsComponent } from "./Components/TabsComponent";
import { GraphTabHeader } from "./Components/GraphTabHeader";
import { CtaButton, CtaWrapper, SearchOverviewGraphContainer, TabContentStyle } from "./Components/StyledComponents";
import {
    BRANDED_NON_BRANDED,
    BRANDED_NON_BRANDED_CATEGORY,
    ORGANIC_PAID,
    ORGANIC_PAID_CATEGORY,
    searchTrafficGraphTabsConfig,
    SEARCH_TYPE,
    SEARCH_TYPE_CATEGORY,
    timeGranularityList,
    MMX_ORGANIC_PAID,
} from "./Helpers/SearchOverviewGraphConfig";
import { AvgVisitDurationGraph } from "./Metrics/AvgVisitDurationGraph";
import { BounceRateGraph } from "./Metrics/BounceRateGraph";
import { PagesPerVisitGraph } from "./Metrics/PagesPerVisitGraph";
import { TrafficShareGraph } from "./Metrics/TrafficShareGraph";
import { useActionCreators } from "./SearchOverviewGraphActions";
import type { ICategory } from "./SearchOverviewGraphReducer";
import { initState, searchOverviewGraphReducer } from "./SearchOverviewGraphReducer";
import { i18nFilter } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import { searchOverviewContext } from "./SearchOverviewGraphContext";
import { useCurrentStateAndParams } from "@uirouter/react";
import {
    transformCompareMMXData,
    transformSingleMMXData,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Helpers/SearchOverviewGraphDataParsers";
import { allTrackers } from "app/services/track/track";

interface ISearchOverviewGraphProps {
    isMobileWeb: boolean;
    isDesktop: boolean;
    isSingle: boolean;
    isTotal: boolean;
}
const SearchOverviewGraphInner: FunctionComponent<ISearchOverviewGraphProps> = (props) => {
    const { params } = useCurrentStateAndParams();
    const { isSingle, isMobileWeb, isDesktop, isTotal } = props;
    const [state, dispatch] = useReducer(
        searchOverviewGraphReducer,
        {
            ...params,
            isSingle,
            isMobileWeb,
            isTotal,
            isDesktop,
            selectedMetric: searchTrafficGraphTabsConfig[0],
        } as unknown,
        initState,
    );
    const actionCreators = useActionCreators(dispatch);
    const { fetchDataStart, fetchDataEnd, saveData, setActiveTab, setError } = actionCreators;
    const fetchService = DefaultFetchService.getInstance();
    const {
        selectedMetricTab,
        country,
        includeSubDomains,
        keys,
        granularity,
        webSource,
        from,
        to,
        isWindow,
        category,
        isLoading,
        totals,
        isError,
        channel,
        isMonthsToDateActive,
        duration,
    } = state;
    const queryParams = useMemo(
        () => ({
            country,
            includeSubDomains,
            keys,
            ...(!!granularity && { timeGranularity: granularity }),
            webSource,
            from,
            to,
            isWindow,
            ...(isMonthsToDateActive && getMonthToDateEndPointValue()),
        }),
        [country, includeSubDomains, keys, granularity, webSource, from, to, isWindow, isMonthsToDateActive],
    );
    const Metrics = [TrafficShareGraph, AvgVisitDurationGraph, PagesPerVisitGraph, BounceRateGraph]; // metrics to be rendered as Tabs

    const isCategorySupportsMonthToDate = category.id === ORGANIC_PAID;
    const isMonthToDateSupportedForDuration = DurationService.isMonthToDateSupportedForDuration(duration);
    const isMonthsToDateSupported = isCategorySupportsMonthToDate && isMonthToDateSupportedForDuration;

    const getEndPoint = (category: ICategory) => {
        switch (category.id) {
            case ORGANIC_PAID:
                return "widgetApi/TrafficSourcesSearch/SearchOrganicPaidOverview/BarChart";
            case BRANDED_NON_BRANDED:
                return "widgetApi/TrafficSourcesSearchV2/BrandedKeywords/BarChart";
            case SEARCH_TYPE:
                return "widgetApi/TrafficSourcesSearch/SearchTrafficByChannel/BarChart";
            case MMX_ORGANIC_PAID:
                return `widgetApi/MarketingMix${isTotal ? "Total" : "Mobile"}/TrafficSourcesOverview/BarChart`;
        }
    };

    const getData = useCallback(
        (cat = category, params = queryParams) => {
            const endPoint = getEndPoint(cat);
            return fetchService.get(endPoint, params);
        },
        [queryParams, category, channel],
    );

    const getMetricTotals = async (dataPromise) => {
        function calcTotals(res) {
            return searchTrafficGraphTabsConfig.reduce((totals, { name }) => {
                return {
                    ...totals,
                    [name]: res?.Data?.[name]?.Data?.Total?.["All Channels"],
                };
            }, {});
        }

        const shouldDisplayMetricTotals = isSingle && isDesktop;
        if (!shouldDisplayMetricTotals) {
            return null;
        }
        if (category === ORGANIC_PAID_CATEGORY) {
            return calcTotals(await dataPromise);
        }
        return calcTotals(await getData(ORGANIC_PAID_CATEGORY));
    };

    const fetchData = async (timeStamp, params) => {
        try {
            fetchDataStart();
            const enrichedDataPromises = Object.values(timeGranularityList).reduce((results, { name }) => {
                const queryParams = { ...params, timeGranularity: name };
                const dataPromiseOrganicPaid = getData(ORGANIC_PAID_CATEGORY, queryParams);
                const dataPromiseBrandedNonBranded = getData(BRANDED_NON_BRANDED_CATEGORY, {
                    ...queryParams,
                    latest: undefined,
                });
                const dataPromiseSearchType = getData(SEARCH_TYPE_CATEGORY, {
                    ...queryParams,
                    latest: undefined,
                });
                return {
                    ...results,
                    [name]: [dataPromiseOrganicPaid, dataPromiseBrandedNonBranded, dataPromiseSearchType],
                };
            }, {});
            const [dataPromiseOrganicPaid, dataPromiseBrandedNonBranded, dataPromiseSearchType] =
                enrichedDataPromises[String(granularity)];

            const dataPromise: any =
                category.id === ORGANIC_PAID_CATEGORY.id
                    ? dataPromiseOrganicPaid
                    : category.id === BRANDED_NON_BRANDED_CATEGORY.id
                      ? dataPromiseBrandedNonBranded
                      : dataPromiseSearchType;
            const promiseArray = [
                dataPromise,
                getMetricTotals(dataPromise),
                ...enrichedDataPromises[timeGranularityList.weekly.name],
                ...enrichedDataPromises[timeGranularityList.monthly.name],
            ];
            const results = await Promise.allSettled(promiseArray);
            const [
                response,
                totals,
                ORGANIC_PAIDDataWeekly,
                BRANDED_NON_BRANDEDDataWeekly,
                SEARCH_TYPEDataWeekly,
                ORGANIC_PAIDDataMonthly,
                BRANDED_NON_BRANDEDDataMonthly,
                SEARCH_TYPEDataMonthly,
            ] = results as { value?: any }[];
            if (timeStamp !== fetchDataRaceConditionProtected.current) {
                // newer call is in progress...
                // protection against race async race condition
                return;
            }
            const rawData = response.value?.Data ? response.value.Data : response.value;
            saveData(
                {
                    ...rawData,
                    enrichedData: {
                        Weekly: {
                            ORGANIC_PAIDData: ORGANIC_PAIDDataWeekly.value,
                            BRANDED_NON_BRANDEDData: BRANDED_NON_BRANDEDDataWeekly.value,
                            SEARCH_TYPEData: SEARCH_TYPEDataWeekly.value,
                        },
                        Monthly: {
                            ORGANIC_PAIDData: ORGANIC_PAIDDataMonthly.value,
                            BRANDED_NON_BRANDEDData: BRANDED_NON_BRANDEDDataMonthly.value,
                            SEARCH_TYPEData: SEARCH_TYPEDataMonthly.value,
                        },
                    },
                },
                totals.value,
            );
        } catch (error) {
            setError();
        } finally {
            fetchDataEnd();
        }
    };

    const fetchDataTotalMobile = async () => {
        try {
            fetchDataStart();
            const dataPromise = getData(category, { ...queryParams, latest: undefined });
            const response = (await dataPromise) as { Data: any };
            saveData(response.Data ? response.Data : response, null);
        } catch (error) {
            setError();
        } finally {
            fetchDataEnd();
        }
    };

    const fetchMMXData = async () => {
        try {
            fetchDataStart();
            const response = (await getData(category, { ...queryParams, latest: undefined })) as {
                Data: any;
            };
            const transformedData = isSingle
                ? transformSingleMMXData(response, selectedMetricTab)
                : transformCompareMMXData(response, selectedMetricTab);
            saveData(transformedData.Data ? transformedData.Data : response, null);
        } catch (error) {
            setError();
        } finally {
            fetchDataEnd();
        }
    };

    const fetchDataRaceConditionProtected = useRef<number>(null);
    useEffect(() => {
        const timeStamp = Date.now();
        fetchDataRaceConditionProtected.current = timeStamp;
        const params =
            isMonthToDateSupportedForDuration && isMonthsToDateActive
                ? queryParams
                : { ...queryParams, latest: undefined };
        switch (webSource) {
            case devicesTypes.MOBILE:
            case devicesTypes.TOTAL:
                category.id === MMX_ORGANIC_PAID ? fetchMMXData() : fetchDataTotalMobile();
                break;
            default:
                fetchData(timeStamp, params);
        }
    }, [queryParams, category, channel]);

    ////////////////////////////////////////////

    const graphTabs = useMemo(() => {
        // in case rawData changed
        if (isSingle) {
            return searchTrafficGraphTabsConfig.map((tab) => {
                return {
                    ...tab,
                    value: totals?.[tab.name],
                };
            });
        }
        return searchTrafficGraphTabsConfig;
    }, [totals]);

    const isNoData = () => {
        if (isLoading) {
            return false;
        }
        return isSingle
            ? !state.rawData?.[selectedMetricTab.name]?.Data?.Total["All Channels"]
            : !(_.sum(Object.values(state.rawData?.[selectedMetricTab.name]?.["All Channels"]?.Total ?? {})) > 0);
    };

    const finalContext = useMemo(
        () => ({
            ...state,
            actions: actionCreators,
            getSiteColor: chosenSitesService.getSiteColor,
            getData,
            chosenSites: chosenSitesService.get(),
            filtersRow: <FiltersRow />,
            emptyState: isError ? <ErrorComponent /> : isNoData() ? <NoDataComponent isSingle={isSingle} /> : null,
        }),
        [state, actionCreators],
    );

    const setSelectedTab = (selectedTab) => {
        allTrackers.dispatchTrackEvent(
            "Metric Button",
            "click",
            `Over Time Graph/Search Overview/${selectedTab.name || ""}`,
        );
        setActiveTab(selectedTab);
    };

    const ctaClicked = () => {
        void proNavigator.go("organicsearch_websites_v2_traffic_keywords", {
            ...proNavigator.getParams(),
        });
    };
    const getNoInsightsReasonKey = () => {
        if (isMobileWeb) {
            return "search.overview.insights.no.insights.due.to.mobile";
        }
        if (isTotal) {
            return "search.overview.insights.no.insights.due.to.total";
        }
        if (!isSingle) {
            return "search.overview.insights.no.insights.due.to.compare";
        }
        return null;
    };

    return (
        state.rawData && (
            <searchOverviewContext.Provider value={finalContext}>
                <SearchOverviewGraphContainer>
                    <GraphTabHeader
                        isMobileWeb={isMobileWeb}
                        isSingle={isSingle}
                        queryParams={isMonthsToDateActive ? queryParams : { ...queryParams, latest: undefined }}
                        params={params}
                        isMonthsToDateSupported={isMonthsToDateSupported}
                    />
                    <SearchTrafficInsights
                        setActiveTab={setActiveTab}
                        graphTabs={graphTabs}
                        noInsightsReasonKey={getNoInsightsReasonKey()}
                    />
                    <div ref={state.chartRef}>
                        {isDesktop ? (
                            <TabsComponent
                                setSelected={setSelectedTab}
                                selectedTab={selectedMetricTab}
                                tabs={graphTabs}
                            >
                                {Metrics.map((MetricName, index) => {
                                    return (
                                        <TabPanel key={index}>
                                            <TabContentStyle>
                                                <MetricName />
                                            </TabContentStyle>
                                        </TabPanel>
                                    );
                                })}
                            </TabsComponent>
                        ) : (
                            <TabContentStyle>
                                <TrafficShareGraph />
                            </TabContentStyle>
                        )}
                    </div>
                </SearchOverviewGraphContainer>
                <CtaWrapper>
                    <CtaButton
                        onClick={ctaClicked}
                        type="flat"
                        label={i18nFilter()("search.trafficandengagement.searchtraffic.cta")}
                    />
                </CtaWrapper>
            </searchOverviewContext.Provider>
        )
    );
};

const propsAreEqual = (prevProps, nextProps) => prevProps.params === nextProps.params;

export const SearchOverviewGraphConnected = React.memo(SearchOverviewGraphInner, propsAreEqual);
